import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {
  Box,
  Chip,
  Grid,
  Link,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import RoleChip from 'components/chip/RoleChip';
import {
  EmptyHint,
  PagedWidgetList,
  StatBreakdownTile,
  StatTile,
  Widget,
  WidgetRow,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { ScreenKey } from 'constant/ScreenPermission';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import { Role } from 'stores/AuthenticationStoreAdapter';
import {
  AdminNoticeResult,
  NOTICE_TYPE_KEY,
  NoticeType,
  listAdminNotice,
} from 'stores/NoticeAdminStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';

const ACCENT = '#00695c';

/** 表示順を固定したロール一覧 */
const ROLE_ORDER: Role[] = ['ADMIN', 'MANAGER', 'BACK_OFFICE', 'GENERAL', 'DEVELOPER'];

/** 通知種別ごとの遷移先パス（通知管理の各タブ。App.tsx のルート定義に一致させる） */
const NOTICE_TYPE_PATH: Record<'NOTICE' | 'INFO' | 'IMPORTANT', string> = {
  NOTICE: '/management/notice/list',
  INFO: '/management/notice/info',
  IMPORTANT: '/management/notice/important',
};

/**
 * 管理ドメインのトップ（ヒーロー + ウィジェットダッシュボード）。
 * ユーザー・通知・年度の管理業務への導線と概況を提供する。
 * - 期限が近い通知（お知らせ・重要なお知らせ）
 * - ロール別ユーザー数
 * - KPI（ユーザー / 通知 / 現在年度）
 * - 機能ナビ（ユーザー / 通知 / 年度）
 */
const ManagementHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, users, financialYearData } = useAuth();
  const { allowedScreens } = userSession;
  const canUser = allowedScreens.includes(ScreenKey.MGMT_USER);
  const canNotice = allowedScreens.includes(ScreenKey.MGMT_NOTICE);
  const canFinancialYear = allowedScreens.includes(ScreenKey.MGMT_FINANCIAL_YEAR);

  const current = financialYearData.current;

  // ウィジェット群の表示切り替え（ユーザー系 / 通知系）。全く別機能なのでビューを分ける。
  // 権限に応じて初期ビューを決める（ユーザー権限があればユーザー、無ければ通知）。
  const defaultView: 'user' | 'notice' = canUser ? 'user' : 'notice';
  // ダッシュボード状態を user_profile(AUTO) で復元/保存する
  const [dashboard, setDashboard, dashboardRestored] = useDashboardProfile(
    'management-home-dashboard',
    {
      view: defaultView,
    },
  );
  const view = dashboard.view;
  const setView = React.useCallback(
    (next: 'user' | 'notice') => setDashboard((prev) => ({ ...prev, view: next })),
    [setDashboard],
  );

  // 復元した view が現在の権限で選べない場合は既定ビューへフォールバック（選択肢消失ガード）。
  React.useEffect(() => {
    if (!dashboardRestored) return;
    const allowed: ('user' | 'notice')[] = [];
    if (canUser) allowed.push('user');
    if (canNotice) allowed.push('notice');
    if (allowed.length > 0 && !allowed.includes(view)) {
      setView(defaultView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardRestored, view, canUser, canNotice]);

  const [notices, setNotices] = React.useState<AdminNoticeResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // 通知系ウィジェットの元データを取得する。初回ロードとヒーローの更新ボタンで共用。
  // ユーザー・年度は AuthContext 由来のため、この Home が保持する通知データを再取得する。
  const reload = React.useCallback(async () => {
    if (!canNotice) {
      setLoading(false);
      return;
    }
    // 全通知を取得し、種別（個別通知/お知らせ/重要なお知らせ）×未解決で集計する
    try {
      const list = await listAdminNotice({}).catch((): AdminNoticeResult[] => []);
      setNotices(list);
    } finally {
      setLoading(false);
    }
  }, [canNotice]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  // 種別ごとの未解決件数（isResolved === false）
  const unresolvedCountByType = React.useCallback(
    (type: NoticeType) => notices.filter((n) => n.noticeType === type && !n.isResolved).length,
    [notices],
  );

  // 未解決の合計（お知らせ + 重要なお知らせのみ）。
  // 個別通知（NOTICE）は管理者側で「解決」の概念が無く isResolved が意味を持たないため除外する。
  const unresolvedTotal = React.useMemo(
    () =>
      unresolvedCountByType(NOTICE_TYPE_KEY.INFO) +
      unresolvedCountByType(NOTICE_TYPE_KEY.IMPORTANT),
    [unresolvedCountByType],
  );

  // 期限が近い通知（期限あり・昇順）。ウィジェットのリスト表示用に保持する
  const deadlineNotices = React.useMemo(
    () =>
      notices
        .filter((n) => n.deadline)
        .sort((a, b) => (a.deadline ?? '').localeCompare(b.deadline ?? '')),
    [notices],
  );

  // 種別ごとの期限あり通知件数
  const deadlineCountByType = React.useCallback(
    (type: NoticeType) => deadlineNotices.filter((n) => n.noticeType === type).length,
    [deadlineNotices],
  );

  // お知らせ／重要なお知らせ（INFO/IMPORTANT）のユーザーごとの未解決件数。
  // unresolvedUserIds は INFO/IMPORTANT に対して埋まる（個別通知 NOTICE は管理者側で解決の概念が
  // 無く unresolvedUserIds を持たないため対象外）。各通知の unresolvedUserIds を横断で数え上げる。
  const unresolvedByUser = React.useMemo(() => {
    const map = new Map<string, number>();
    notices
      .filter(
        (n) => n.noticeType === NOTICE_TYPE_KEY.INFO || n.noticeType === NOTICE_TYPE_KEY.IMPORTANT,
      )
      .forEach((n) => {
        (n.unresolvedUserIds ?? []).forEach((uid) => {
          map.set(uid, (map.get(uid) ?? 0) + 1);
        });
      });
    return Array.from(map.entries())
      .map(([userId, count]) => ({
        userId,
        count,
        userName: users.find((u) => u.userId === userId)?.userName ?? userId,
      }))
      .sort((a, b) => b.count - a.count);
  }, [notices, users]);

  const roleCounts = React.useMemo(() => {
    const map = new Map<Role, number>();
    users.forEach((u) => u.roles.forEach((r) => map.set(r, (map.get(r) ?? 0) + 1)));
    return ROLE_ORDER.filter((r) => (map.get(r) ?? 0) > 0).map((r) => ({
      role: r,
      count: map.get(r) ?? 0,
    }));
  }, [users]);

  // ユーザー系ビュー（ユーザー数 KPI ＋ ロール別ユーザー数）
  const renderUserView = () => (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid size={{ xs: 12, md: 4 }}>
        <StatTile
          icon={<PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />}
          label="ユーザー"
          value={users.length}
          suffix="名"
          color={ACCENT}
          loading={false}
          onClick={() => navigate('/management/user')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Widget
          title="ロール別ユーザー数"
          action={
            <Link
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => navigate('/management/user')}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              ユーザー管理
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Link>
          }
        >
          {roleCounts.length === 0 ? (
            <EmptyHint text="ユーザー情報がありません" />
          ) : (
            <Stack spacing={0.5}>
              {roleCounts.map(({ role, count }) => (
                <Stack
                  key={role}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => navigate(`/management/user?role=${role}`)}
                  sx={{
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.5,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <RoleChip role={role} size="small" />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {count} 名
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Widget>
      </Grid>
    </Grid>
  );

  // 通知系ビュー（未解決/期限あり KPI 内訳 ＋ 期限が近い通知 ＋ ユーザー別未解決）
  const renderNoticeView = () => (
    <Stack spacing={2.5}>
      {/* 通知 KPI: 未解決（お知らせ/重要）内訳・期限あり内訳 */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StatBreakdownTile
            icon={<MarkEmailUnreadOutlinedIcon sx={{ fontSize: 16 }} />}
            label="未解決の通知（お知らせ・重要）"
            total={unresolvedTotal}
            color="#1565c0"
            loading={loading}
            breakdown={[
              {
                label: 'お知らせ',
                value: unresolvedCountByType(NOTICE_TYPE_KEY.INFO),
                onClick: () => navigate(`${NOTICE_TYPE_PATH.INFO}?unresolved=true`),
              },
              {
                label: '重要なお知らせ',
                value: unresolvedCountByType(NOTICE_TYPE_KEY.IMPORTANT),
                onClick: () => navigate(`${NOTICE_TYPE_PATH.IMPORTANT}?unresolved=true`),
              },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <StatBreakdownTile
            icon={<NewReleasesOutlinedIcon sx={{ fontSize: 16 }} />}
            label="期限あり通知"
            total={deadlineNotices.length}
            color="#ed6c02"
            loading={loading}
            breakdown={[
              {
                label: '個別通知',
                value: deadlineCountByType(NOTICE_TYPE_KEY.NOTICE),
                onClick: () => navigate(`${NOTICE_TYPE_PATH.NOTICE}?hasDeadline=true`),
              },
              {
                label: 'お知らせ',
                value: deadlineCountByType(NOTICE_TYPE_KEY.INFO),
                onClick: () => navigate(`${NOTICE_TYPE_PATH.INFO}?hasDeadline=true`),
              },
              {
                label: '重要なお知らせ',
                value: deadlineCountByType(NOTICE_TYPE_KEY.IMPORTANT),
                onClick: () => navigate(`${NOTICE_TYPE_PATH.IMPORTANT}?hasDeadline=true`),
              },
            ]}
          />
        </Grid>
      </Grid>

      {/* 通知ウィジェット: 期限が近い通知 / ユーザー別未解決 を横並び */}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 6 }}>
          <Widget
            title="期限が近い通知"
            action={
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => navigate(`${NOTICE_TYPE_PATH.IMPORTANT}?hasDeadline=true`)}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
              >
                通知管理
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Link>
            }
          >
            {loading ? (
              <Stack spacing={1}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={44} />
                ))}
              </Stack>
            ) : deadlineNotices.length === 0 ? (
              <EmptyHint text="期限が設定された通知はありません" />
            ) : (
              <PagedWidgetList
                items={deadlineNotices}
                keyOf={(n) => n.noticeId}
                renderItem={(n) => (
                  <WidgetRow
                    accentColor={ACCENT}
                    onClick={() =>
                      navigate(
                        `${
                          NOTICE_TYPE_PATH[n.noticeType as 'NOTICE' | 'INFO' | 'IMPORTANT'] ??
                          NOTICE_TYPE_PATH.IMPORTANT
                        }?hasDeadline=true`,
                      )
                    }
                  >
                    <NotificationsActiveOutlinedIcon
                      sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {n.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        期限: {n.deadline ? formatDate(n.deadline) : '—'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={n.isBroadcast ? '全体' : '個別'}
                      sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                    />
                  </WidgetRow>
                )}
              />
            )}
          </Widget>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Widget
            title="未解決（ユーザー別・お知らせ/重要）"
            action={
              <Link
                component="button"
                variant="caption"
                underline="hover"
                onClick={() => navigate(`${NOTICE_TYPE_PATH.IMPORTANT}?unresolved=true`)}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
              >
                通知管理
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Link>
            }
          >
            {loading ? (
              <Stack spacing={1}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={44} />
                ))}
              </Stack>
            ) : unresolvedByUser.length === 0 ? (
              <EmptyHint text="未解決のお知らせ・重要なお知らせはありません" />
            ) : (
              <PagedWidgetList
                items={unresolvedByUser}
                keyOf={(u) => u.userId}
                renderItem={(u) => (
                  <WidgetRow
                    accentColor="#1565c0"
                    onClick={() => navigate(`${NOTICE_TYPE_PATH.IMPORTANT}?unresolved=true`)}
                  >
                    <MarkEmailUnreadOutlinedIcon
                      sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {u.userName}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`未解決 ${u.count}`}
                      sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                    />
                  </WidgetRow>
                )}
              />
            )}
          </Widget>
        </Grid>
      </Grid>
    </Stack>
  );

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーロー */}
        <DomainHero
          eyebrow="Administration"
          title="Run the Back Office"
          tagline="Manage users and their roles, broadcast notices, and roll the financial year — the controls that keep everything running."
          palette={HERO_PALETTES.management}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        {/* ヘッダー: ビュー切り替えトグル（ウィジェット化）＋ 現在年度（横幅固定）。
            高さは各要素の中身に合わせる（stretch しない）ので上下に余白が出ない。 */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          justifyContent="space-between"
        >
          {canUser && canNotice && (
            <Box
              sx={{
                width: { xs: '100%', sm: 320 },
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                p: 0.75,
                bgcolor: 'background.paper',
              }}
            >
              <ToggleButtonGroup
                value={view}
                exclusive
                fullWidth
                size="small"
                onChange={(_e, v) => {
                  if (v) setView(v);
                }}
                sx={{
                  width: '100%',
                  gap: 0.75,
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    border: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    py: 1,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'action.selected' },
                    },
                  },
                }}
              >
                <ToggleButton value="user">
                  <PeopleAltOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
                  ユーザー
                </ToggleButton>
                <ToggleButton value="notice">
                  <CampaignOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
                  通知
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {canFinancialYear && (
            <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
              <StatTile
                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />}
                label="現在年度"
                value={current ? `${current.financialYear}期` : '—'}
                suffix={current ? `${current.month}月` : ''}
                color="#6a1b9a"
                loading={false}
                onClick={() => navigate('/management/financial-year')}
              />
            </Box>
          )}
        </Stack>

        {/* ===== ビュー本体（両権限ありのときは左右スワイプで切り替え） ===== */}
        {canUser && canNotice ? (
          // スライドトラック: 2ビューを横に並べ、view に応じて translateX で切り替える。
          // 横方向のみクリップ（overflowX: hidden）し、縦は visible にして
          // 内部タイルのホバー時の浮き（translateY(-2px)）が上端で切れないようにする。
          // 浮き分を吸収するため上下に軽いパディングを持たせる。
          <Box sx={{ overflowX: 'hidden', overflowY: 'visible', px: 0.5, mx: -0.5, py: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                width: '200%',
                transition: 'transform 0.3s ease',
                transform: view === 'user' ? 'translateX(0)' : 'translateX(-50%)',
              }}
            >
              {/* 左: ユーザービュー */}
              <Box sx={{ width: '50%', flexShrink: 0, pr: 0.25 }}>{renderUserView()}</Box>
              {/* 右: 通知ビュー */}
              <Box sx={{ width: '50%', flexShrink: 0, pl: 0.25 }}>{renderNoticeView()}</Box>
            </Box>
          </Box>
        ) : canUser ? (
          renderUserView()
        ) : canNotice ? (
          renderNoticeView()
        ) : null}
      </Stack>
    </Box>
  );
};

export default ManagementHome;
